import { useEffect, useId, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { Link } from 'react-router-dom'
import ProductIcon from '../icons/ProductIcon'
import { OpsButton } from './ops'
import { useAnchoredPosition } from './overlayPosition'

const cx = (...parts) => parts.filter(Boolean).join(' ')

// Above 15 items a menu gets a search field and scrolls (NN/g: a searchable
// list above about 15 items). The height cap is assumed: 360px, or 60% of a
// short window such as 1280×720 at the extra-large interface scale.
export const MENU_SEARCH_THRESHOLD = 15
const SEARCHING_MAX_HEIGHT = 'min(360px, 60vh)'

const isEntry = (item) => item.type !== 'separator' && item.type !== 'group'

/* Splits the flat item list into sections: a group starts at { type: 'group' }
   and runs to the next group or separator. */
function sections(items) {
  const out = []
  let current = null
  let separator = false
  for (const item of items) {
    if (item.type === 'separator') { separator = true; current = null; continue }
    if (item.type === 'group' || !current) {
      current = { label: item.type === 'group' ? item.label : null, entries: [], separatorBefore: separator }
      out.push(current)
      separator = false
      if (item.type === 'group') continue
    }
    current.entries.push(item)
  }
  return out.filter(section => section.entries.length > 0)
}

/**
 * Menu button (WAI-ARIA menu button pattern). Items render only while open.
 *
 * items: { id, label, onSelect, icon?, tone?: 'danger', disabled?, reason?, href? }
 *        | { type: 'separator' } | { type: 'group', label }
 * trigger: optional render function receiving the trigger's props
 *          (ref, aria attributes, handlers) — spread them onto one button.
 * search: show the search field regardless of the item count, for a list that
 *         stands for a longer one (the shell's overflow menu).
 */
export default function Menu({ label, items = [], trigger, align = 'end', size = 'md', className, search }) {
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const triggerRef = useRef(null)
  const menuRef = useRef(null)
  const searchRef = useRef(null)
  const initialFocus = useRef('first')
  const menuId = useId()
  const searchable = search ?? items.filter(isEntry).length > MENU_SEARCH_THRESHOLD
  const position = useAnchoredPosition(triggerRef, open, { align })

  const visible = useMemo(() => {
    if (!searchable || !query.trim()) return items
    const needle = query.trim().toLowerCase()
    return items.filter(item => isEntry(item) && String(item.label).toLowerCase().includes(needle))
  }, [items, query, searchable])

  const menuItems = () => [...(menuRef.current?.querySelectorAll('[role="menuitem"]') ?? [])]

  const close = (returnFocus = true) => {
    setOpen(false)
    setQuery('')
    if (returnFocus) triggerRef.current?.focus()
  }

  const openWith = (focus) => {
    initialFocus.current = focus
    setOpen(true)
  }

  useEffect(() => {
    if (!open) return
    if (searchable && initialFocus.current !== 'last') { searchRef.current?.focus(); return }
    const all = menuItems()
    ;(initialFocus.current === 'last' ? all.at(-1) : all[0])?.focus()
  }, [open, searchable])

  useEffect(() => {
    if (!open) return undefined
    const onPointer = (event) => {
      if (menuRef.current?.contains(event.target) || triggerRef.current?.contains(event.target)) return
      close(false)
    }
    document.addEventListener('mousedown', onPointer)
    return () => document.removeEventListener('mousedown', onPointer)
  })

  const move = (event) => {
    const all = menuItems()
    if (all.length === 0) return
    const index = all.indexOf(document.activeElement)
    let next = null
    if (event.key === 'ArrowDown') next = all[(index + 1) % all.length]
    else if (event.key === 'ArrowUp') next = index <= 0 ? all.at(-1) : all[index - 1]
    else if (event.key === 'Home') next = all[0]
    else if (event.key === 'End') next = all.at(-1)
    if (next) { event.preventDefault(); next.focus() }
  }

  const onMenuKeyDown = (event) => {
    if (event.key === 'Escape') { event.preventDefault(); event.stopPropagation(); close(); return }
    if (event.key === 'Tab') { close(false); return }
    if (event.target === searchRef.current) {
      if (event.key === 'ArrowDown') { event.preventDefault(); menuItems()[0]?.focus() }
      return
    }
    move(event)
  }

  const select = (item, event) => {
    if (item.disabled) { event.preventDefault(); return }
    close()
    item.onSelect?.(event)
  }

  const triggerProps = {
    ref: triggerRef,
    'aria-haspopup': 'menu',
    'aria-expanded': open,
    'aria-controls': open ? menuId : undefined,
    onClick: () => (open ? close(false) : openWith('first')),
    onKeyDown: (event) => {
      if (event.key === 'ArrowDown' || event.key === 'Enter' || event.key === ' ') { event.preventDefault(); openWith('first') }
      else if (event.key === 'ArrowUp') { event.preventDefault(); openWith('last') }
    },
  }

  const renderEntry = (item) => {
    const reasonId = item.disabled && item.reason ? `${menuId}-${item.id}-reason` : undefined
    const body = (
      <>
        {item.icon && <ProductIcon name={item.icon} size="inline" />}
        <span className="flex min-w-0 flex-col">
          <span>{item.label}</span>
          {reasonId && <span id={reasonId} className="text-[12px] text-[var(--app-text-2)]">{item.reason}</span>}
        </span>
      </>
    )
    const common = {
      role: 'menuitem',
      tabIndex: -1,
      'aria-disabled': item.disabled || undefined,
      'aria-describedby': reasonId,
      // border-0 bg-transparent: Tailwind's preflight is off (it would fight
      // CoreUI's base styles), so a menu item button would keep the browser's
      // own frame and fill.
      className: cx(
        'flex w-full min-h-[36px] items-center gap-2 rounded-[var(--app-radius)] border-0 bg-transparent px-3 py-1.5 text-left text-[13px] no-underline',
        'focus:bg-[var(--slate-4)] focus:outline-none hover:bg-[var(--slate-4)]',
        item.disabled ? 'cursor-not-allowed text-[var(--app-text-muted)]' : item.tone === 'danger' ? 'text-[var(--app-crit)]' : 'text-[var(--app-text)]',
      ),
      onClick: (event) => select(item, event),
    }
    return (
      <li key={item.id} role="none">
        {item.href && !item.disabled
          ? <Link to={item.href} {...common}>{body}</Link>
          : <button type="button" {...common}>{body}</button>}
      </li>
    )
  }

  const shown = sections(visible)

  return (
    <>
      {trigger ? trigger(triggerProps) : (
        <OpsButton {...triggerProps} tone="quiet" size={size} aria-label={label} className={className}>
          <ProductIcon name="ui/ellipsis" size="inline" />
        </OpsButton>
      )}
      {open && createPortal(
        <div
          ref={menuRef}
          className="z-[1060] min-w-[180px] max-w-[min(360px,calc(100vw-16px))] rounded-[var(--app-radius)] border border-[var(--app-border)] bg-[var(--app-overlay)] p-1"
          style={{ ...position, boxShadow: 'var(--app-shadow-1)' }}
          onKeyDown={onMenuKeyDown}
        >
          {searchable && (
            <input
              ref={searchRef}
              type="search"
              aria-label={`Search ${label}`}
              aria-controls={menuId}
              placeholder="Search…"
              spellCheck={false}
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              className="mb-1 w-full rounded-[var(--app-radius)] border border-[var(--app-border)] bg-[var(--app-surface)] px-2 py-1.5 text-[13px] text-[var(--app-text)]"
            />
          )}
          <ul
            id={menuId}
            role="menu"
            aria-label={label}
            className="m-0 list-none p-0"
            style={searchable ? { maxHeight: SEARCHING_MAX_HEIGHT, overflowY: 'auto' } : undefined}
          >
            {shown.map((section, index) => (
              <li key={index} role="none">
                {section.separatorBefore && index > 0 && <div role="separator" className="my-1 border-t border-[var(--app-border)]" />}
                {section.label && <div role="presentation" id={`${menuId}-g${index}`} className="px-3 pt-2 pb-1 text-[11px] text-[var(--app-text-2)]">{section.label}</div>}
                <ul role="group" aria-labelledby={section.label ? `${menuId}-g${index}` : undefined} className="m-0 list-none p-0">
                  {section.entries.map(renderEntry)}
                </ul>
              </li>
            ))}
            {shown.length === 0 && <li role="none" className="px-3 py-2 text-[13px] text-[var(--app-text-2)]">No matches</li>}
          </ul>
        </div>,
        document.body,
      )}
    </>
  )
}
